import type { BackgroundStyle, BrandColors, ButtonStyle, RestaurantSettings } from '../types/workspace';

export const DEFAULT_BRAND_COLORS: BrandColors = {
  primary: '#f4ba4b',
  accent: '#d6652b',
  surface: '#1f0d0a',
  background: '#120605',
  text: '#f7f1e6',
};

export const DEFAULT_BACKGROUND_STYLE: BackgroundStyle = 'soft-gradient';
export const DEFAULT_BUTTON_STYLE: ButtonStyle = 'pill';
export const DEFAULT_CURRENCY_CODE = 'ETB';
export const DEFAULT_CURRENCY_SYMBOL = 'ETB';
export const DEFAULT_CALL_TO_ACTION_LABEL = 'Call to Order';
export const DEFAULT_MAPS_URL = 'https://maps.app.goo.gl/m6TpJUumjcbuC9Dd7?g_st=ic';
const FALLBACK_BRAND_SVG = encodeURIComponent(`
<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 88 88">
  <defs>
    <linearGradient id="gate" x1="0%" y1="0%" x2="100%" y2="100%">
      <stop offset="0%" stop-color="#f6c75a" />
      <stop offset="100%" stop-color="#c17812" />
    </linearGradient>
  </defs>
  <rect x="6" y="6" width="76" height="76" rx="24" fill="#1a0c08" stroke="#f4ba4b" stroke-width="2" />
  <path d="M14 20C25 10 63 10 74 20" fill="none" stroke="#f6c75a" stroke-linecap="round" stroke-width="2.4" />
  <path d="M18 72C28 79 60 79 70 72" fill="none" stroke="#d99118" stroke-linecap="round" stroke-width="2.2" />
  <path d="M20 58V28L30 18H58L68 28V58H54V39C54 33 49 28 44 28C39 28 34 33 34 39V58H20Z" fill="url(#gate)" />
  <path d="M39 58V40C39 36.5 41.7 34 44 34C46.3 34 49 36.5 49 40V58" fill="#1a0c08" />
</svg>
`);
export const DEFAULT_LOGO_IMAGE = `data:image/svg+xml;charset=UTF-8,${FALLBACK_BRAND_SVG}`;
export const DEFAULT_FAVICON_IMAGE = DEFAULT_LOGO_IMAGE;

export const normalizeHexColor = (value: string, fallback: string) => {
  const normalized = value.trim();
  return /^#([0-9a-f]{3}|[0-9a-f]{6})$/i.test(normalized) ? normalized : fallback;
};

export const normalizeBrandColors = (colors?: Partial<BrandColors> | null): BrandColors => ({
  primary: normalizeHexColor(colors?.primary ?? '', DEFAULT_BRAND_COLORS.primary),
  accent: normalizeHexColor(colors?.accent ?? '', DEFAULT_BRAND_COLORS.accent),
  surface: normalizeHexColor(colors?.surface ?? '', DEFAULT_BRAND_COLORS.surface),
  background: normalizeHexColor(colors?.background ?? '', DEFAULT_BRAND_COLORS.background),
  text: normalizeHexColor(colors?.text ?? '', DEFAULT_BRAND_COLORS.text),
});

export const normalizeBackgroundStyle = (value?: string | null): BackgroundStyle =>
  value === 'solid' || value === 'photo-overlay' ? value : DEFAULT_BACKGROUND_STYLE;

export const normalizeButtonStyle = (value?: string | null): ButtonStyle =>
  value === 'rounded' || value === 'soft' ? value : DEFAULT_BUTTON_STYLE;

export const normalizeRestaurantSettings = (settings: RestaurantSettings): RestaurantSettings => ({
  ...settings,
  logoImage:
    (typeof settings.logoImage === 'string' ? settings.logoImage.trim() : '') || DEFAULT_LOGO_IMAGE,
  faviconImage:
    (typeof settings.faviconImage === 'string' ? settings.faviconImage.trim() : '') ||
    DEFAULT_FAVICON_IMAGE,
  currencyCode:
    (typeof settings.currencyCode === 'string' ? settings.currencyCode.trim().toUpperCase() : '') ||
    DEFAULT_CURRENCY_CODE,
  currencySymbol:
    (typeof settings.currencySymbol === 'string' ? settings.currencySymbol.trim() : '') ||
    DEFAULT_CURRENCY_SYMBOL,
  callToActionLabel:
    (typeof settings.callToActionLabel === 'string' ? settings.callToActionLabel.trim() : '') ||
    DEFAULT_CALL_TO_ACTION_LABEL,
  mapsUrl:
    (typeof settings.mapsUrl === 'string' ? settings.mapsUrl.trim() : '') || DEFAULT_MAPS_URL,
  brandColors: normalizeBrandColors(settings.brandColors),
  backgroundStyle: normalizeBackgroundStyle(settings.backgroundStyle),
  buttonStyle: normalizeButtonStyle(settings.buttonStyle),
});

export const hexToRgb = (hex: string) => {
  const normalized = hex.replace('#', '');
  const safe = normalized.length === 3
    ? normalized
        .split('')
        .map((char) => `${char}${char}`)
        .join('')
    : normalized;
  const value = Number.parseInt(safe, 16);
  return {
    r: (value >> 16) & 255,
    g: (value >> 8) & 255,
    b: value & 255,
  };
};

export const formatPrice = (price: number, settings: Pick<RestaurantSettings, 'currencySymbol' | 'currencyCode'>) => {
  const suffix = settings.currencySymbol?.trim() || settings.currencyCode?.trim() || DEFAULT_CURRENCY_SYMBOL;
  return `${price} ${suffix}`;
};

export const getButtonRadiusClass = (buttonStyle: ButtonStyle) => {
  if (buttonStyle === 'rounded') return '1.2rem';
  if (buttonStyle === 'soft') return '1rem';
  return '9999px';
};

export const getShellBackground = (
  style: BackgroundStyle,
  colors: BrandColors,
  coverImage?: string,
) => {
  if (style === 'solid') {
    return colors.background;
  }

  if (style === 'photo-overlay' && coverImage?.trim()) {
    return `linear-gradient(180deg, rgba(8, 5, 4, 0.84), rgba(17, 9, 7, 0.9)), radial-gradient(circle at top right, ${colors.accent}33, transparent 28%), url("${coverImage}") center/cover fixed`;
  }

  return `radial-gradient(circle at top left, ${colors.accent}33, transparent 26%), radial-gradient(circle at top right, ${colors.primary}2b, transparent 30%), linear-gradient(180deg, ${colors.background} 0%, ${colors.surface} 40%, ${colors.background} 100%)`;
};
